import { useCallback, useEffect } from 'react';
import { Outlet } from 'react-router';
import NetworkStatusNotification from '@/components/NetworkStatusNotification/NetworkStatusNotification';
import { AudioContextProvider } from '@/contexts/audioContext';
import { AuthContextProvider } from '@/contexts/authContext';
import { FeatureFlagContextProvider } from '@/contexts/featureFlagContext';
import { AppModalContextProvider } from '@/contexts/modalContext';
import { QueryClientConfigProvider } from '@/contexts/queryContext';
import { GlobalSWRConfigProvider } from '@/contexts/swrContext/GlobalSWRConfigProvider';
import { useRouteActivitySession } from '@/hooks/useRouteActivitySession';
import { useVisualViewportHeight } from '@/hooks/useVisualViewportHeight';
import { getCurrentSessionInfo } from '@/utils/activitySession';
import { logger } from '@/utils/logger';

/**
 * BaseAppRoot provide global context to the whole app
 */
const BaseAppRoot = () => {
  const handleVisibilityChange = useCallback(() => {
    const { visibilityState } = document;
    const sessionInfo = getCurrentSessionInfo();
    logger.info('Visibility State Changed', { visibilityState, time: new Date(), sessionInfo });
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  useRouteActivitySession();
  useVisualViewportHeight();

  return (
    <QueryClientConfigProvider>
      <GlobalSWRConfigProvider>
        <FeatureFlagContextProvider>
          <AppModalContextProvider>
            <AuthContextProvider>
              <AudioContextProvider>
                <NetworkStatusNotification />
                <Outlet />
              </AudioContextProvider>
            </AuthContextProvider>
          </AppModalContextProvider>
        </FeatureFlagContextProvider>
      </GlobalSWRConfigProvider>
    </QueryClientConfigProvider>
  );
};

export default BaseAppRoot;
